import { isEmpty } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useDeleteSalesUnit,
  useDeleteUploadProductImage,
  useGetProductById,
  usePostUSalesUnit,
  usePutEditProduct,
  usePutEditSalesUnit,
  useUploadProductImage
} from "../../../../api/product";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON, UNIT_TYPES } from "../../../../config/constants";
import { IsRootProduct } from "../../../../config/enums/ProductType.enum";
import { errorView } from "../../../../helpers/ErrorHandler";
import { useConvertToAverageEligibility } from "../../../../hooks/useConvertToAverageEligibility";
import { ProductRegisterOptions } from "../../productRegister/services/ProductRegisterOptions";
import {
  ProductDetails,
  ProductEditModalProps,
  ProductEditViewModel,
  SalesUnit
} from "../types/productEditView.types";

/**
 * Owns every piece of state, data fetching and mutation handling the legacy
 * `ProductEditModal` kept inline (see
 * src/screens/product/productEdit/ProductEditModal.tsx). Presentational
 * components under `components/` consume the flat `ProductEditViewModel`
 * this hook returns instead of touching `api/product` or either underlying
 * react-hook-form instance themselves — same split as
 * `useProductRegisterViewModel`.
 *
 * Ported logic-for-logic, including two easy-to-miss legacy quirks kept
 * exactly as-is:
 *  - `onAddUnit`'s success handler resets the MAIN product form (not the
 *    unit form) with unit-shaped keys, which technically blanks
 *    product_name/registration_number for a moment — the subsequent
 *    `refetch()` re-populates them via the `initiateProduct` effect before
 *    it's visibly noticeable, same as it does in the legacy modal.
 *  - `onSave`'s `statusCloseModal` ref: a normal Save closes/reinitializes
 *    immediately, without waiting for `editProductData` to resolve, while an
 *    average-product-toggle-triggered save (`ref.current` set to `false` in
 *    `onConfirmAverageChange`) waits for the mutation to succeed first.
 *    `saveAndNavigate`'s form reset only fires on the normal-save branch
 *    (`statusCloseModal.current === true`) — a prior port of this hook had
 *    that condition inverted, which reset the forms on the average-toggle
 *    path instead and skipped the reset on ordinary saves; fixed to match
 *    the legacy modal's actual behavior.
 */
export const useProductEditViewModel = ({
  visibleModal,
  setVisibleModal,
  productId
}: ProductEditModalProps): ProductEditViewModel => {
  const [addProductUnit, setProductUnit] = useState<SalesUnit[]>([]);
  const [productType, setProductType] = useState<string>("PROCESS");
  const [enableSizeEditStatus, setEnableSizeEditStatus] = useState<boolean>(false);
  const [enableSizeEdit, setEnableSizeEdit] = useState<SalesUnit | null>(null);
  const [defaultType, setDefaultType] = useState<string>(UNIT_TYPES[0].value);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<SalesUnit | null>(null);
  const [isRootProduct, setIsRootProduct] = useState<string>(IsRootProduct.NO);
  const [visibleAverageConfirmModal, setVisibleAverageConfirmModal] = useState<boolean>(false);
  const [pendingAverageValue, setPendingAverageValue] = useState<string | null>(null);
  const isRootProductYes = isRootProduct === IsRootProduct.YES;
  const statusCloseModal = useRef(true);

  const mainForm = useForm<ProductDetails>({ mode: "all" });
  const { handleSubmit, reset, getValues, setValue: setProductValue } = mainForm;

  const unitForm = useForm<SalesUnit>();
  const { reset: resetUnitSize, setValue: setUnitValue } = unitForm;

  const { data: productDetails, isLoading: productDetailsLoading, refetch } =
    useGetProductById(productId);

  const { isLoading: editProductLoading, mutate: editProductData } = usePutEditProduct();
  const { isLoading: editProductUnitLoading, mutate: editSalesUnit } = usePutEditSalesUnit();
  const { isLoading: addProductUnitLoading, mutate: addNewSalesUnit } = usePostUSalesUnit();
  const { isLoading: isLoadingProductImage, mutate: uploadProductImage } = useUploadProductImage();
  const { isLoading: isLoadingProductImageDelete, mutate: deleteProductImage } =
    useDeleteUploadProductImage();
  const { mutate: deleteSalesUnit, isLoading: isLoadingDeleteProductSalesUnit } = useDeleteSalesUnit();

  const {
    isEligible,
    reason: averageToggleDisabledReason,
    isLoading: isAverageEligibilityLoading
  } = useConvertToAverageEligibility(productDetails?._id);
  const isAverageToggleDisabled = !isRootProductYes && !isEligible;

  const setUnitSizeUpdate = (data: SalesUnit) => {
    setEnableSizeEdit(data);
    setEnableSizeEditStatus(true);
    setUnitValue("sales_unit_name", data.sales_unit_name);
    setUnitValue("sales_unit_size", data.sales_unit_size);
    setUnitValue("sales_unit_type", data.sales_unit_type);
    setUnitValue("ean_barcode", data.ean_barcode);
  };

  const updateUnitType = (value: string) => {
    setDefaultType(value);
    setUnitValue("sales_unit_type", value);
  };

  const addUnitData = (data: SalesUnit) => {
    const salesUnit = {
      product_id: productDetails?._id,
      sales_units: [
        {
          sales_unit_size: data?.sales_unit_size ?? "",
          sales_unit_type: defaultType ?? "",
          ean_barcode: data?.ean_barcode ?? "",
          sales_unit_name: data?.sales_unit_name?.trim() ?? ""
        }
      ]
    };
    addNewSalesUnit(salesUnit, {
      onSuccess: () => {
        refetch();
        setDefaultType(UNIT_TYPES[0].value);
        // Mirrors the legacy modal exactly: this resets the MAIN product
        // form (not the unit form) with unit-shaped keys — see the
        // preserved-quirks note above.
        reset({
          sales_unit_size: "",
          sales_unit_type: "",
          ean_barcode: "",
          sales_unit_name: ""
        } as unknown as ProductDetails);
        resetUnitSize();
      }
    });
  };

  const deleteUnitData = (data: SalesUnit) => {
    deleteSalesUnit(
      { productId, salesUnitId: data?._id },
      {
        onSuccess: () => {
          refetch();
          setDeleteData(null);
          setVisibleDeleteModal(false);
        }
      }
    );
  };

  const onConfirmDeleteUnit = () => {
    if (!deleteData) return;
    if (deleteData?.has_drawing) {
      errorView("This product already has a digital twin (Mapping), it cannot be deleted.");
      return;
    }
    deleteUnitData(deleteData);
  };

  const editUnitSize = (data: SalesUnit) => {
    const updateValue = {
      product_id: productDetails?._id,
      sales_unit_id: enableSizeEdit?._id,
      sales_unit_name: (data.sales_unit_name || "").trim(),
      sales_unit_size: data.sales_unit_size,
      sales_unit_type: data.sales_unit_type,
      ean_barcode: data.ean_barcode
    };
    editSalesUnit(updateValue, {
      onSuccess: () => {
        resetUnitSize();
        setDefaultType(UNIT_TYPES[0].value);
        refetch();
      }
    });
  };

  const initiateProduct = () => {
    if (productDetails) {
      setProductValue("product_name", productDetails.product_name);
      setProductValue("registration_number", productDetails.registration_number ?? "");
      setProductValue("product_other_info", productDetails.product_other_info ?? "");
      setProductUnit(productDetails.sales_units ?? []);
      setProductType(productDetails.product_type);
      setIsRootProduct(productDetails.is_average_product ? IsRootProduct.YES : IsRootProduct.NO);
    }
  };

  useEffect(() => {
    initiateProduct();
    return () => {
      setEnableSizeEditStatus(false);
      setProductUnit([]);
      setProductType("PROCESS");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, visibleModal]);

  const enableUnitSizeAdd = useMemo(() => {
    return (
      (isRootProduct === IsRootProduct.YES && addProductUnit.length === 0) ||
      isRootProduct === IsRootProduct.NO
    );
  }, [isRootProduct, addProductUnit]);

  const checkProductSaveCondition = (data: ProductDetails): boolean => {
    if (isEmpty(data.product_name.trim())) {
      errorView("Product name is required");
      return true;
    } else if (addProductUnit.length <= 0) {
      errorView("Please add product sizes");
      return true;
    }
    return false;
  };

  // Unlike the register flow's `isRootProductBool`, the edit flow never
  // gates this on `DISABLE_IS_ROOT_PRODUCT_BUTTON` — preserved as its own
  // (inconsistent, but legacy) definition.
  const isRootProductBool = useMemo(() => isRootProduct === IsRootProduct.YES, [isRootProduct]);

  const saveAndNavigate = () => {
    if (!statusCloseModal.current) return;
    reset();
    resetUnitSize();
  };

  const onConfirmAverageChange = () => {
    if (!pendingAverageValue) return;
    statusCloseModal.current = false;
    setIsRootProduct(pendingAverageValue);
    setVisibleAverageConfirmModal(false);
    setPendingAverageValue(null);
  };

  const onCancelAverageChange = () => {
    setVisibleAverageConfirmModal(false);
    setPendingAverageValue(null);
  };

  const onSubmit = (data: ProductDetails) => {
    if (!checkProductSaveCondition(data)) {
      const { product_name, product_other_info, registration_number } = data;
      const productData = {
        _id: productDetails?._id,
        product_name: product_name.trim(),
        product_other_info: product_other_info?.trim() ?? "",
        registration_number,
        product_type: productType,
        is_average_product: isRootProductBool
      };
      editProductData(productData, {
        onSuccess: () => {
          saveAndNavigate();
          setVisibleModal(true);
        }
      });
      // Mirrors the legacy modal exactly: on a normal Save (ref still
      // `true`), this fires immediately rather than waiting for
      // `editProductData` above to resolve — see the preserved-quirks note
      // above.
      if (statusCloseModal.current) {
        setVisibleModal(true);
        initiateProduct();
      }
    }
  };

  useEffect(() => {
    if (isRootProduct && !statusCloseModal.current) {
      (async () => {
        await handleSubmit(onSubmit)();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRootProduct, statusCloseModal.current]);

  const onRequestAverageToggle = (value: string) => {
    if (isAverageToggleDisabled) return;
    setPendingAverageValue(value);
    setVisibleAverageConfirmModal(true);
  };

  const validateUnitName = (value: string): true | string => {
    const clean = value.trim().toLowerCase();
    if (clean.length > 20) return "Maximum 20 characters allowed";

    const isDuplicate = addProductUnit.some((unit, index) => {
      if (enableSizeEditStatus && enableSizeEdit && addProductUnit[index]?._id === enableSizeEdit._id) {
        return false;
      }
      return unit.sales_unit_name?.trim().toLowerCase() === clean;
    });

    return isDuplicate ? "Unit name already exists." : true;
  };

  const cancelProduct = () => {
    setVisibleModal(true);
    reset();
    resetUnitSize();
  };

  const saveProduct = () => {
    onSubmit(getValues());
  };

  return {
    productDetails,

    methods: mainForm,
    productType,
    onProductTypeChange: setProductType,
    showAverageProductToggle: !DISABLE_IS_ROOT_PRODUCT_BUTTON,
    isRootProduct,
    isRootProductYes,
    isAverageToggleDisabled,
    averageToggleDisabledReason,
    isAverageEligibilityLoading,
    onRequestAverageToggle,
    onImageUpload: file => {
      if (file) {
        uploadProductImage(
          { product_id: productDetails?._id, file },
          { onSuccess: () => refetch() }
        );
      }
    },
    onImageDelete: () => {
      deleteProductImage({ product_id: productDetails?._id }, { onSuccess: () => refetch() });
    },

    unitMethods: unitForm,
    defaultUnitType: defaultType,
    onDefaultUnitTypeChange: updateUnitType,
    unitTypeOptions: ProductRegisterOptions.unitTypes(),
    addProductUnit,
    enableUnitSizeAdd,
    enableSizeEditStatus,
    validateUnitName,
    onAddUnit: addUnitData,
    onUpdateUnit: editUnitSize,
    onEditUnitStart: setUnitSizeUpdate,
    onDeleteUnitRequest: unit => {
      setDeleteData(unit);
      setVisibleDeleteModal(true);
    },

    showProductMatching: isRootProductYes && Boolean(productDetails?._id) && !DISABLE_IS_ROOT_PRODUCT_BUTTON,

    isSaving:
      productDetailsLoading ||
      editProductLoading ||
      editProductUnitLoading ||
      addProductUnitLoading ||
      isLoadingProductImage ||
      isLoadingProductImageDelete ||
      isLoadingDeleteProductSalesUnit,
    saveTitle: isRootProductYes ? "Save and Exit" : "Save changes",
    onSave: () => handleSubmit(saveProduct)(),
    onCancel: cancelProduct,

    visibleDeleteModal,
    onCloseDeleteModal: () => setVisibleDeleteModal(false),
    onCancelDeleteModal: () => {
      setVisibleDeleteModal(false);
      setDeleteData(null);
    },
    onConfirmDeleteUnit,

    visibleAverageConfirmModal,
    averageConfirmMessage:
      pendingAverageValue === IsRootProduct.YES
        ? "Are you sure you want to enable Average Impact Product? This will link sales units for average calculations."
        : "Are you sure you want to disable Average Impact Product? This will unlink all connected sales units.",
    onConfirmAverageChange,
    onCancelAverageChange
  };
};
